import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import { AlloyButton } from 'cat-ecommerce-alloy';
import useLogin from '@app/components/login/hooks/useLogin';
import useRegister from '@app/components/login/hooks/useRegister';
import useCheckout from '@app/components/pages/checkout/shopping-cart/ShoppingCartPage/hooks/useCheckout.js';
import useDealerLocator from '@app/components/dealer-locator/useDealerLocator.js';
import ProceedToCheckoutButton from '@app/components/pages/checkout/shopping-cart/OrderItems/common/ProceedToCheckoutButton/ProceedToCheckoutButton';
import { getSessionStorage } from '@app/utils';
import { CONSENT_SESSION_KEY } from '@app/constants/commonConstants';
import styles from './GuestEmptyCartDealerEsiteOptions.module.scss';
import { useContext } from 'react';
import { ConsentAcceptanceContext } from '@app/components/common/ConsentAcceptanceModalContext';

const GuestEmptyCartDealerEsiteOptions = () => {
  const [t] = useTranslation();
  const { login } = useLogin();
  const modalProps = useContext(ConsentAcceptanceContext);
  const { checkoutPage, isCheckoutButtonDisabled } = useCheckout();
  const { showNewRegistrationLink } = useRegister();
  const { showDealerLocator } = useDealerLocator();
  const isCatCorp = useSelector(s => s.common.isCatCorp);
  const current = useSelector(s => s.orders.current);
  const currentOrder = useSelector(s => s.orders.byId?.[current]);
  const numItemsInCart = currentOrder?.ascendingOrderItems?.length ?? 0;
  const shopByCategoryUrl = useSelector(s => s.categories.shopByCategory);
  const dealerRegistrationURL = useSelector(
    s => s.common.dealerRegistrationURL
  );
  /* Dealer specific registration feature flag */
  const pCCUserRegistrationRedirectFlag = useSelector(
    s => s?.featureFlag?.PCCUserRegistrationRedirect
  );
  const isConsentSelected =
    getSessionStorage(CONSENT_SESSION_KEY) &&
    JSON.parse(getSessionStorage(CONSENT_SESSION_KEY));

  const openDealerLocator = () => showDealerLocator({ isRegistration: true });

  const handleSignInClick = () => {
    login();
  };

  const handleContinueAsGuest = () => {
    if (numItemsInCart > 0) {
      checkoutPage();
    } else {
      window.location.href = shopByCategoryUrl;
    }
  };

  /**
   * Routes to new registration page based upon the feature flag.
   */
  const routeToRegisterPage = event => {
    event.preventDefault();
    if (isConsentSelected) {
      pCCUserRegistrationRedirectFlag
        ? showNewRegistrationLink()
        : (window.location = dealerRegistrationURL);
    } else {
      modalProps.open();
    }
  };

  return (
    <section className="d-flex flex-column flex-lg-row">
      <div className="pe-lg-3">
        <div className={cx('h3 w-50 d-print-none')}>
          {t('QUICK_ORDER_SIGN_IN_TEXT_2')}
        </div>
        <p className="u-fs--12 mb-4 mb-lg-3 u-color--MediumGray">
          {t('SIGN_IN_MESSAGE')}
        </p>
        <AlloyButton
          buttonType="dark"
          className="w-100 w-lg-auto"
          size="dynamic"
          onClick={handleSignInClick}
        >
          {t('QUICK_ORDER_SIGN_IN_TEXT_2')}
        </AlloyButton>
        <AlloyButton
          buttonType="ghost"
          className="w-100 w-lg-auto"
          size="dynamic"
          onClick={handleContinueAsGuest}
          disabled={isCheckoutButtonDisabled}
        >
          {t('1631_CONTINUE_AS_GUEST')}
        </AlloyButton>
        <ProceedToCheckoutButton className="d-print-none d-none w-100" />
      </div>
      <div
        className={cx(
          styles['dealer-esite-options__divider'],
          'mx-lg-3 my-lg-0 my-3 mx-0'
        )}
      />
      <div className="ps-lg-3">
        <h4 className={cx('h3 d-print-none')}>
          {t('GLOBALLOGIN_HAVING_NEW_ACCOUNT')}
        </h4>
        <p className="u-fs--12 mb-0 mb-lg-2 u-color--MediumGray">
          {t('CREATE_CAT_ACCOUNT_GUEST')}
        </p>
        <ul
          className={cx(styles['dealer-esite-options__sign-in-list'], 'mb-2')}
        >
          <li className="u-color--MediumGray m-0 u-fs--12">
            <p className="u-fs--12 u-color--MediumGray mb-0">
              {t('SEE_DEALER_PRICES')}
            </p>
          </li>
          <li className="u-color--MediumGray m-0 u-fs--12">
            <p className="u-fs--12 u-color--MediumGray mb-0">
              {t('ACCESS_PAYMENT_METHODS')}
            </p>
          </li>
          <li className="u-color--MediumGray m-0 u-fs--12">
            <p className="u-fs--12 u-color--MediumGray mb-0">
              {t('QUICKLY_CHECKOUT')}
            </p>
          </li>
          <li className="u-color--MediumGray m-0 u-fs--12">
            <p className="u-fs--12 u-color--MediumGray mb-0">
              {t('VIEW_ORDER_HISTORY')}
            </p>
          </li>
        </ul>
        <AlloyButton
          buttonType="dark"
          className="w-100 w-lg-auto"
          size="dynamic"
          onClick={isCatCorp ? openDealerLocator : e => routeToRegisterPage(e)}
        >
          {t('CREATE_AN_ACCOUNT')}
        </AlloyButton>
        <div
          className={cx(
            styles['dealer-esite-options__divider'],
            'd-lg-none mx-lg-3 my-lg-0 my-3 mx-0'
          )}
        />
      </div>
    </section>
  );
};

export default GuestEmptyCartDealerEsiteOptions;
